import { useEffect, useState } from "react";
import api, { formatApiError, uploadFileChunked } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Upload, Sparkles, Loader2, Eye, PenLine } from "lucide-react";
import AiImageButton from "@/components/AiImageButton";
import ArticleBody from "@/components/ArticleBody";

export const EMPTY_ARTICLE = { title: "", slug: "", excerpt: "", meta_description: "", sector: "", category: "Cas de figure", image: "", content: "", status: "draft" };

const F = ({ label, hint, children }) => (
  <div><Label className="text-xs uppercase tracking-wider text-[#475569]">{label}</Label><div className="mt-1.5">{children}</div>{hint && <p className="text-[11px] text-[#94A3B8] mt-1">{hint}</p>}</div>
);

const AiWriter = ({ onDraft }) => {
  const [topic, setTopic] = useState("");
  const [sector, setSector] = useState("");
  const [context, setContext] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (topic.trim().length < 5) return toast.error("Décrivez le sujet de l'article (5 caractères minimum).");
    setBusy(true);
    try { const { data } = await api.post("/admin/articles/generate", { topic, sector, context }); onDraft(data); toast.success("Brouillon rédigé : relisez-le avant de publier."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Rédaction impossible."); }
    setBusy(false);
  };
  return (
    <div data-testid="article-ai-writer" className="bg-[#F8F6F0] border border-[#E0C173]/50 p-4 space-y-3">
      <p className="text-sm font-medium text-[#1B2A4A] flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#C9A85A]" /> Rédiger avec l'IA</p>
      <div className="grid sm:grid-cols-[1fr_200px] gap-3">
        <Input data-testid="article-ai-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Sujet, ex. Vendre un restaurant à Marseille" className="rounded-none bg-white border-gray-300" />
        <Input data-testid="article-ai-sector" value={sector} onChange={(e) => setSector(e.target.value)} placeholder="Secteur (facultatif)" className="rounded-none bg-white border-gray-300" />
      </div>
      <Textarea data-testid="article-ai-context" value={context} onChange={(e) => setContext(e.target.value)} placeholder="Précisions : cas de figure, mots-clés, ville… (facultatif)" className="rounded-none bg-white border-gray-300 min-h-[60px]" />
      <Button type="button" data-testid="article-ai-generate" onClick={run} disabled={busy} className="rounded-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Rédaction en cours (≈ 30 s)…</> : <><Sparkles className="w-4 h-4 mr-2" /> Générer le brouillon</>}
      </Button>
    </div>
  );
};

const ImageInput = ({ form, setForm }) => {
  const [progress, setProgress] = useState(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { setProgress(0); const url = await uploadFileChunked(file, setProgress); setForm((f) => ({ ...f, image: url })); }
    catch { toast.error("Échec de l'import"); } finally { setProgress(null); e.target.value = ""; }
  };
  return (
    <div className="grid sm:grid-cols-[180px_1fr] gap-4 items-start">
      <div className="aspect-[16/10] bg-[#F8F6F0] border border-gray-200 overflow-hidden">{form.image && <img data-testid="article-image-preview" src={form.image} alt="" className="w-full h-full object-cover" />}</div>
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          <label data-testid="article-image-upload" className="inline-flex items-center gap-2 cursor-pointer bg-[#1B2A4A] text-white text-sm px-4 py-2 rounded-full hover:bg-[#2a3d63] transition-colors">
            <Upload className="w-4 h-4" /> {progress != null ? `Import… ${progress}%` : "Importer"}
            <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={progress != null} />
          </label>
          <AiImageButton testid="article-image-ai" title={form.title} sector={form.sector} description={form.excerpt} onGenerated={(url) => setForm((f) => ({ ...f, image: url }))} />
        </div>
        <Input data-testid="article-field-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="… ou URL de l'image" className="rounded-none border-gray-300" />
      </div>
    </div>
  );
};

export default function ArticleDialog({ article, onOpenChange, onSaved }) {
  const [form, setForm] = useState(EMPTY_ARTICLE);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (article) { setForm({ ...EMPTY_ARTICLE, ...article }); setPreview(false); } }, [article]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const editing = !!article?.article_id;
  const close = () => {
    const dirty = JSON.stringify({ ...EMPTY_ARTICLE, ...article }) !== JSON.stringify(form);
    if (!dirty || window.confirm("Les modifications non enregistrées seront perdues. Fermer quand même ?")) onOpenChange(false);
  };

  const save = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 5) return toast.error("Le titre est obligatoire (5 caractères minimum).");
    if (form.content.trim().length < 50) return toast.error("Le contenu de l'article est trop court.");
    setBusy(true);
    const payload = Object.fromEntries(Object.keys(EMPTY_ARTICLE).map((k) => [k, form[k] ?? ""]));
    try {
      if (editing) await api.put(`/admin/articles/${article.article_id}`, payload); else await api.post("/admin/articles", payload);
      toast.success(form.status === "published" ? "Article publié" : "Brouillon enregistré"); onSaved(); onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!article} onOpenChange={(v) => (v ? onOpenChange(v) : close())}>
      <DialogContent className="max-w-4xl rounded-none max-h-[94vh] overflow-y-auto" data-testid="article-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl text-[#1B2A4A]">{editing ? "Modifier l'article" : "Nouvel article"}</DialogTitle></DialogHeader>
        <form onSubmit={save} noValidate className="space-y-4">
          {!editing && <AiWriter onDraft={(d) => setForm((f) => ({ ...f, ...d, sector: d.sector || f.sector }))} />}
          <F label="Titre *"><Input data-testid="article-field-title" value={form.title} onChange={set("title")} className="rounded-none border-gray-300" /></F>
          <div className="grid sm:grid-cols-3 gap-4">
            <F label="Catégorie"><Input data-testid="article-field-category" value={form.category} onChange={set("category")} className="rounded-none border-gray-300" /></F>
            <F label="Secteur"><Input data-testid="article-field-sector" value={form.sector} onChange={set("sector")} className="rounded-none border-gray-300" /></F>
            <F label="Adresse (URL)" hint="Laissé vide : créée à partir du titre"><Input data-testid="article-field-slug" value={form.slug} onChange={set("slug")} placeholder="vendre-mon-entreprise" className="rounded-none border-gray-300" /></F>
          </div>
          <F label="Chapeau / résumé"><Textarea data-testid="article-field-excerpt" value={form.excerpt} onChange={set("excerpt")} className="rounded-none border-gray-300 min-h-[60px]" /></F>
          <F label="Description Google" hint={`${form.meta_description.length}/160 caractères conseillés — texte affiché dans les résultats de recherche`}><Textarea data-testid="article-field-meta" value={form.meta_description} onChange={set("meta_description")} className="rounded-none border-gray-300 min-h-[50px]" /></F>
          <F label="Image"><ImageInput form={form} setForm={setForm} /></F>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-[#475569]">Contenu *</Label>
              <button type="button" data-testid="article-preview-toggle" onClick={() => setPreview(!preview)} className="text-xs inline-flex items-center gap-1 text-[#1B2A4A] hover:text-[#C9A85A]">{preview ? <><PenLine className="w-3.5 h-3.5" /> Modifier</> : <><Eye className="w-3.5 h-3.5" /> Aperçu</>}</button>
            </div>
            {preview ? <div className="mt-1.5 border border-gray-200 p-6 max-h-[480px] overflow-y-auto"><ArticleBody content={form.content} /></div> : (
              <Textarea data-testid="article-field-content" value={form.content} onChange={set("content")} className="rounded-none mt-1.5 border-gray-300 min-h-[320px] font-mono text-xs leading-relaxed" />
            )}
            <p className="text-[11px] text-[#94A3B8] mt-1">Mise en forme : ligne vide entre paragraphes · « ## » pour un intertitre · « - » pour une liste · **gras**</p>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <div className="flex items-center gap-3"><Switch data-testid="article-field-status" checked={form.status === "published"} onCheckedChange={(v) => setForm({ ...form, status: v ? "published" : "draft" })} /><span className="text-sm text-[#1B2A4A]">{form.status === "published" ? "Publié sur le site" : "Brouillon (non visible)"}</span></div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={close} className="rounded-full">Annuler</Button>
              <Button data-testid="article-save" type="submit" disabled={busy} className="rounded-full bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold px-6">{busy ? "Enregistrement…" : "Enregistrer"}</Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
